import React, { lazy as reactLazy, Suspense, useEffect, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { isBetaBlocked } from './utils/betaAreas';
import SiteAccessGate from './components/SiteAccessGate';
import { ThemeProvider } from './context/ThemeContext';
import { BrandingProvider, useBranding } from './context/BrandingContext';
import { ClientProvider, useClient } from './context/ClientContext';
import { PlanPreviewProvider } from './context/PlanPreviewContext';
import { Toaster } from './components/ui/sonner';
import { trackPageView } from './utils/analytics';
import CommercialTracker from './components/CommercialTracker';
import { scrollToTop } from './utils/scrollUtils';
import './App.css';

/* ─── Resilient lazy() ───────────────────────────────────────────────
 * After a production redeploy, an already-open session requests chunk
 * files whose content-hash filenames no longer exist. server.js then
 * serves index.html (HTML) for the missing .js, so the dynamic import
 * fails with a ChunkLoadError and every client-side navigation dies.
 * This wrapper auto-recovers by reloading ONCE (fresh chunk manifest);
 * a genuine repeat failure within 10s is rethrown to the ErrorBoundary
 * so real bugs still surface. Drop-in replacement for React.lazy — all
 * existing `lazy(() => import(...))` calls get this behaviour for free. */
const CHUNK_RELOAD_KEY = 'cpdforge_chunk_reload_ts';
const lazy = (factory) =>
  reactLazy(() =>
    factory().catch((err) => {
      const isChunkErr =
        err && (err.name === 'ChunkLoadError' ||
                /Loading chunk|dynamically imported module|Failed to fetch/i.test(err.message || ''));
      const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
      if (isChunkErr && Date.now() - last > 10000) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        window.location.reload();
        return new Promise(() => {}); // hold the render while the page reloads
      }
      throw err;
    })
  );

/* ─── Global Error Boundary ─── */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorInfo: null };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('[CPDForge] Uncaught UI error:', error, info?.componentStack);
    this._errorPath = window.location.pathname;
    this.setState({ errorInfo: info });
  }
  componentDidMount() {
    // Recover automatically when the user navigates to a different route
    // (React Router <Link> uses pushState, so poll the pathname cheaply).
    this._navCheck = setInterval(() => {
      if (this.state.hasError && this._errorPath && window.location.pathname !== this._errorPath) {
        this._errorPath = null;
        this.setState({ hasError: false, errorInfo: null });
      }
    }, 400);
  }
  componentWillUnmount() { clearInterval(this._navCheck); }
  render() {
    if (this.state.hasError) {
      // SEO-friendly fallback for public pages (for Googlebot)
      const isPublicPath = window.location.pathname === '/' || 
                           window.location.pathname.startsWith('/features') ||
                           window.location.pathname.startsWith('/pricing') ||
                           window.location.pathname.startsWith('/about') ||
                           window.location.pathname.includes('alternative') ||
                           window.location.pathname.includes('guide') ||
                           window.location.pathname.includes('compliance');
      
      if (isPublicPath) {
        return (
          <div className="min-h-screen bg-white dark:bg-slate-950">
            <header className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 sticky top-0 z-50">
              <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <img src="/cpdforge-icon.webp" alt="CPDForge AI" className="w-9 h-9 rounded-lg" />
                  <span className="text-lg font-bold text-slate-900 dark:text-white">CPDForge AI</span>
                </div>
              </div>
            </header>
            <main className="max-w-7xl mx-auto px-6 py-16">
              <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-6">
                {window.location.pathname === '/' ? 'AI Compliance Training Platform' : 
                 window.location.pathname.includes('features') ? 'Features — CPDForge AI' :
                 window.location.pathname.includes('pricing') ? 'Pricing — CPDForge AI' :
                 window.location.pathname.includes('alternative') ? 'Compliance Training Alternative' :
                 'CPDForge AI'}
              </h1>
              <div className="prose prose-slate dark:prose-invert max-w-none">
                <p className="text-lg text-slate-600 dark:text-slate-400 mb-4">
                  CPDForge AI is the AI compliance training platform that helps organizations create, improve, and manage compliance training at scale.
                </p>
                <p className="text-base text-slate-600 dark:text-slate-400 mb-4">
                  Generate structured compliance courses in minutes, improve content automatically with AI Review, 
                  monitor regulatory changes, track learner progress, and deploy training across your organization.
                </p>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">Key Features</h2>
                <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                  <li>🤖 AI-powered course generation in under 5 minutes</li>
                  <li>✨ Non-destructive content improvement with AI Review</li>
                  <li>📊 Regulatory change monitoring and alerts</li>
                  <li>👥 Learner tracking and compliance reporting</li>
                  <li>📤 SCORM, PDF, and PowerPoint export</li>
                  <li>🏢 Multi-client and multi-site support</li>
                </ul>
                <div className="mt-8">
                  <a href="/" className="inline-block px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors">
                    Go to Homepage
                  </a>
                </div>
              </div>
            </main>
          </div>
        );
      }

      // Standard error UI for authenticated/dashboard pages
      return (
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950 px-6" data-testid="error-boundary-fallback">
          <div className="text-center max-w-md">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-950/40 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Something went wrong</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">An unexpected error occurred. Please refresh to continue.</p>
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
              className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
              data-testid="error-boundary-refresh-btn"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ─── Eagerly loaded: landing page (critical path) ─── */
import Landing from './pages/Landing';

/* ─── Lazy-loaded: public pages ─── */
const Pricing = lazy(() => import('./pages/Pricing'));
const Features = lazy(() => import('./pages/Features'));
const Support = lazy(() => import('./pages/Support'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const SharedCourseView = lazy(() => import('./pages/SharedCourseView'));
const ProgrammeReview = lazy(() => import('./pages/ProgrammeReview'));
const VerifyCertificate = lazy(() => import('./pages/VerifyCertificate'));
const VerifySample = lazy(() => import('./pages/VerifySample'));
const DemoCourse = lazy(() => import('./pages/FlagshipDemo'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const About = lazy(() => import('./pages/About'));
const ProfessionalStandardsInstitute = lazy(() => import('./pages/ProfessionalStandardsInstitute'));
const AccreditationHub = lazy(() => import('./pages/AccreditationHub'));
const AccreditationRegistry = lazy(() => import('./pages/AccreditationRegistry'));
const VerificationHub = lazy(() => import('./pages/VerificationHub'));
const WhatIsCPDForge = lazy(() => import('./pages/WhatIsCPDForge'));
const WhatIsAICompliancePlatform = lazy(() => import('./pages/WhatIsAICompliancePlatform'));

/* ─── Lazy-loaded: Knowledge Hub ─── */
const KnowledgeHub = lazy(() => import('./pages/KnowledgeHub'));
const KnowledgeHubArticle = lazy(() => import('./pages/KnowledgeHubArticle'));

/* ─── Lazy-loaded: Public Library ─── */
const PublicLibrary = lazy(() => import('./pages/PublicLibrary'));
const PublicProgrammes = lazy(() => import('./pages/PublicProgrammes'));
const PublicProgrammeDetail = lazy(() => import('./pages/PublicProgrammeDetail'));
const PublicCredentialFamily = lazy(() => import('./pages/PublicCredentialFamily'));
const PublicCredentialsIndex = lazy(() => import('./pages/PublicCredentialsIndex'));
const PublicClusterPage = lazy(() => import('./pages/PublicClusterPage'));
const PublicSegmentPage = lazy(() => import('./pages/PublicSegmentPage'));
const ProgrammePurchaseSuccess = lazy(() => import('./pages/ProgrammePurchaseSuccess'));
const PurchaseAccess = lazy(() => import('./pages/PurchaseAccess'));
const MoneyBackGuarantee = lazy(() => import('./pages/MoneyBackGuarantee'));
const PublicCourseDetail = lazy(() => import('./pages/PublicCourseDetail'));
const PublicPillarPage = lazy(() => import('./pages/PublicPillarPage'));
const CommunityCatalogue = lazy(() => import('./pages/CommunityCatalogue'));
const CommunityProgramme = lazy(() => import('./pages/CommunityProgramme'));
const CommunityCourse = lazy(() => import('./pages/CommunityCourse'));
const CommunityLesson = lazy(() => import('./pages/CommunityLesson'));
const PublicIndustryPage = lazy(() => import('./pages/PublicIndustryPage'));
const PublicRegulationPage = lazy(() => import('./pages/PublicRegulationPage'));
const KnowledgeHubEditor = lazy(() => import('./pages/KnowledgeHubEditor'));

/* ─── Lazy-loaded: AI Governance & Assurance ecosystem hubs (Phase C2) ─── */
const AIPillarHub = lazy(() => import('./pages/ai/AIPillarHub'));
const AIObjectiveHub = lazy(() => import('./pages/ai/AIObjectiveHub'));
const AIStandardHub = lazy(() => import('./pages/ai/AIStandardHub'));
const AIRegulationHub = lazy(() => import('./pages/ai/AIRegulationHub'));
const AIRoleHub = lazy(() => import('./pages/ai/AIRoleHub'));
const SubjectHub = lazy(() => import('./pages/SubjectHub'));

/* ─── Lazy-loaded: SEO landing pages ─── */
const KeepComplianceUpToDate = lazy(() => import('./pages/seo/KeepComplianceUpToDate'));
const RegulatoryChangeTracking = lazy(() => import('./pages/seo/RegulatoryChangeTracking'));
const AIComplianceTrainingPlatform = lazy(() => import('./pages/seo/AIComplianceTrainingPlatform'));
const AIComplianceTrainingSoftware = lazy(() => import('./pages/seo/AIComplianceTrainingSoftware'));
const AITrainingPlatform = lazy(() => import('./pages/seo/AITrainingPlatform'));
const CorporateComplianceTrainingAI = lazy(() => import('./pages/seo/CorporateComplianceTrainingAI'));
const WhyComplianceTrainingFails = lazy(() => import('./pages/seo/WhyComplianceTrainingFails'));
const HowToImproveComplianceTraining = lazy(() => import('./pages/seo/HowToImproveComplianceTraining'));
const ComplianceTrainingEffectiveness = lazy(() => import('./pages/seo/ComplianceTrainingEffectiveness'));
const AICourseGeneratorCompliance = lazy(() => import('./pages/seo/AICourseGeneratorCompliance'));
const AutomatedComplianceTraining = lazy(() => import('./pages/seo/AutomatedComplianceTraining'));
const IHASCOAlternative = lazy(() => import('./pages/seo/IHASCOAlternative'));
const HighSpeedTrainingAlternative = lazy(() => import('./pages/seo/HighSpeedTrainingAlternative'));
const SkillcastAlternative = lazy(() => import('./pages/seo/SkillcastAlternative'));
const TalentLMSAlternative = lazy(() => import('./pages/seo/TalentLMSAlternative'));
const LitmosAlternative = lazy(() => import('./pages/seo/LitmosAlternative'));
const DoceboAlternative = lazy(() => import('./pages/seo/DoceboAlternative'));
const DiligentESGAlternative = lazy(() => import('./pages/seo/DiligentESGAlternative'));
const WorkivaAlternative = lazy(() => import('./pages/seo/WorkivaAlternative'));
const CourseraESGAlternative = lazy(() => import('./pages/seo/CourseraESGAlternative'));
const SpheraAlternative = lazy(() => import('./pages/seo/SpheraAlternative'));
const EnablonAlternative = lazy(() => import('./pages/seo/EnablonAlternative'));
const EcoVadisAlternative = lazy(() => import('./pages/seo/EcoVadisAlternative'));
const PositionGreenAlternative = lazy(() => import('./pages/seo/PositionGreenAlternative'));
const BenchmarkESGAlternative = lazy(() => import('./pages/seo/BenchmarkESGAlternative'));
const NovataAlternative = lazy(() => import('./pages/seo/NovataAlternative'));
const PersefoniAlternative = lazy(() => import('./pages/seo/PersefoniAlternative'));
const UdemyBusinessAlternative = lazy(() => import('./pages/seo/UdemyBusinessAlternative'));
const LinkedInLearningAlternative = lazy(() => import('./pages/seo/LinkedInLearningAlternative'));
const SkillsoftAlternative = lazy(() => import('./pages/seo/SkillsoftAlternative'));
const Go1Alternative = lazy(() => import('./pages/seo/Go1Alternative'));
const OpenSesameAlternative = lazy(() => import('./pages/seo/OpenSesameAlternative'));
const CourseraBusinessAlternative = lazy(() => import('./pages/seo/CourseraBusinessAlternative'));
const EdXBusinessAlternative = lazy(() => import('./pages/seo/EdXBusinessAlternative'));
const PluralsightAlternative = lazy(() => import('./pages/seo/PluralsightAlternative'));
const FutureLearnAlternative = lazy(() => import('./pages/seo/FutureLearnAlternative'));
const AlisonAlternative = lazy(() => import('./pages/seo/AlisonAlternative'));
const HumanFocusAlternative = lazy(() => import('./pages/seo/HumanFocusAlternative'));
const EssentialSkillzAlternative = lazy(() => import('./pages/seo/EssentialSkillzAlternative'));
const AlcumusAlternative = lazy(() => import('./pages/seo/AlcumusAlternative'));
const SafetyhubAlternative = lazy(() => import('./pages/seo/SafetyhubAlternative'));
const RoSPAAlternative = lazy(() => import('./pages/seo/RoSPAAlternative'));
const Praxis42Alternative = lazy(() => import('./pages/seo/Praxis42Alternative'));
const DeltaNetAlternative = lazy(() => import('./pages/seo/DeltaNetAlternative'));
const AstutisAlternative = lazy(() => import('./pages/seo/AstutisAlternative'));
const VinciWorksAlternative = lazy(() => import('./pages/seo/VinciWorksAlternative'));
const NavexAlternative = lazy(() => import('./pages/seo/NavexAlternative'));
const TraliantAlternative = lazy(() => import('./pages/seo/TraliantAlternative'));
const SAI360Alternative = lazy(() => import('./pages/seo/SAI360Alternative'));
const GANIntegrityAlternative = lazy(() => import('./pages/seo/GANIntegrityAlternative'));
const ThreeSixtyFactorsAlternative = lazy(() => import('./pages/seo/ThreeSixtyFactorsAlternative'));
const OneTrustAlternative = lazy(() => import('./pages/seo/OneTrustAlternative'));
const HyperproofAlternative = lazy(() => import('./pages/seo/HyperproofAlternative'));
const LogicGateAlternative = lazy(() => import('./pages/seo/LogicGateAlternative'));
const LearnUponAlternative = lazy(() => import('./pages/seo/LearnUponAlternative'));
const AbsorbLMSAlternative = lazy(() => import('./pages/seo/AbsorbLMSAlternative'));
const ISpringLearnAlternative = lazy(() => import('./pages/seo/ISpringLearnAlternative'));
const CornerstoneAlternative = lazy(() => import('./pages/seo/CornerstoneAlternative'));
const MoodleAlternative = lazy(() => import('./pages/seo/MoodleAlternative'));
const SAPSuccessFactorsAlternative = lazy(() => import('./pages/seo/SAPSuccessFactorsAlternative'));
const ThreeSixtyLearningAlternative = lazy(() => import('./pages/seo/ThreeSixtyLearningAlternative'));
const BestComplianceTrainingPlatforms = lazy(() => import('./pages/seo/BestComplianceTrainingPlatforms'));
const ComplianceTrainingSoftware = lazy(() => import('./pages/seo/ComplianceTrainingSoftware'));
const AICourseCreationCompliance = lazy(() => import('./pages/seo/AICourseCreationCompliance'));
const MicrolearningFeature = lazy(() => import('./pages/seo/MicrolearningFeature'));
const MicrolearningGuide = lazy(() => import('./pages/seo/MicrolearningGuide'));
const MFAGuide = lazy(() => import('./pages/MFAGuide'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));

/* ─── Lazy-loaded: Comparison System (Hub + Pillars) ─── */
const ComparisonHub = lazy(() => import('./pages/compare/ComparisonHub'));
const HealthSafetyPillar = lazy(() => import('./pages/compare/HealthSafetyPillar'));
const ComplianceGRCPillar = lazy(() => import('./pages/compare/ComplianceGRCPillar'));
const LMSPillar = lazy(() => import('./pages/compare/LMSPillar'));
const ESGPillar = lazy(() => import('./pages/compare/ESGPillar'));
const GeneralTrainingPillar = lazy(() => import('./pages/compare/GeneralTrainingPillar'));

/* ─── Lazy-loaded: Assessment Tool ─── */
const AssessmentLanding = lazy(() => import('./pages/assessment/AssessmentLanding'));
const AssessmentFlow = lazy(() => import('./pages/assessment/AssessmentFlow'));
const AssessmentV2 = lazy(() => import('./pages/assessment/AssessmentV2'));
const AssessmentLoading = lazy(() => import('./pages/assessment/AssessmentLoading'));
const AssessmentResults = lazy(() => import('./pages/assessment/AssessmentResults'));
const AssessmentMethodology = lazy(() => import('./pages/assessment/AssessmentMethodology'));

/* ─── Lazy-loaded: Use Case Pages ─── */
const BestComplianceTrainingForSmallBusiness = lazy(() => import('./pages/usecases/BestComplianceTrainingForSmallBusiness'));
const BestComplianceTrainingPlatformForSMEs = lazy(() => import('./pages/usecases/BestComplianceTrainingPlatformForSMEs'));
const BestComplianceTrainingWithAI = lazy(() => import('./pages/usecases/BestComplianceTrainingWithAI'));
const BestComplianceTrainingForFinancialServices = lazy(() => import('./pages/usecases/BestComplianceTrainingForFinancialServices'));
const BestLMSForOnboarding = lazy(() => import('./pages/usecases/BestLMSForOnboarding'));
const BestAccreditedComplianceTraining = lazy(() => import('./pages/usecases/BestAccreditedComplianceTraining'));
const BestESGReportingSoftware = lazy(() => import('./pages/usecases/BestESGReportingSoftware'));
const BestTrainingPlatformsForRemoteTeams = lazy(() => import('./pages/usecases/BestTrainingPlatformsForRemoteTeams'));

/* ─── Lazy-loaded: Learner portal ─── */
const LearnerLogin = lazy(() => import('./pages/LearnerLogin'));
const LearnerSetPassword = lazy(() => import('./pages/LearnerSetPassword'));
const LearnerCourses = lazy(() => import('./pages/LearnerCourses'));
const LearnerCoursePlayer = lazy(() => import('./pages/LearnerCoursePlayer'));

/* ─── Lazy-loaded: Authenticated app shell + pages ─── */
const AppLayout = lazy(() => import('./components/AppLayout'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const PartnerDashboard = lazy(() => import('./pages/PartnerDashboard'));
const GenerateCourse = lazy(() => import('./pages/GenerateCourse'));
const CourseView = lazy(() => import('./pages/CourseView'));
const EditCourse = lazy(() => import('./pages/EditCourse'));
const BrandingSettings = lazy(() => import('./pages/BrandingSettings'));
const CertificateSettings = lazy(() => import('./pages/CertificateSettings'));
const OrganisationProfile = lazy(() => import('./pages/OrganisationProfile'));
const ComplianceReports = lazy(() => import('./pages/ComplianceReports'));
const TemplateLibrary = lazy(() => import('./pages/TemplateLibrary'));
const Learners = lazy(() => import('./pages/Learners'));
const LearnerProfile = lazy(() => import('./pages/LearnerProfile'));
const Billing = lazy(() => import('./pages/Billing'));
const AISettings = lazy(() => import('./pages/AISettings'));
const Team = lazy(() => import('./pages/Team'));
const ClientMembers = lazy(() => import('./pages/ClientMembers'));
const ClientTeams = lazy(() => import('./pages/ClientTeams'));
const Roles = lazy(() => import('./pages/Roles'));
const RoleDetail = lazy(() => import('./pages/RoleDetail'));
const Programmes = lazy(() => import('./pages/Programmes'));
const EducationProgrammes = lazy(() => import('./pages/EducationProgrammes'));
const EducationProgrammeDetail = lazy(() => import('./pages/EducationProgrammeDetail'));
const EducationStudio = lazy(() => import('./pages/EducationStudio'));
const EducationReadiness = lazy(() => import('./pages/EducationReadiness'));
const ProgrammeDetail = lazy(() => import('./pages/ProgrammeDetail'));
const Blueprints = lazy(() => import('./pages/Blueprints'));
const BlueprintDetail = lazy(() => import('./pages/BlueprintDetail'));
const ProgrammeCourses = lazy(() => import('./pages/ProgrammeCourses'));
const OwnerAudit = lazy(() => import('./pages/OwnerAudit'));
const GrowthCommandCentre = lazy(() => import('./pages/GrowthCommandCentre'));
const ClientComplianceOverview = lazy(() => import('./pages/ClientComplianceOverview'));
const RiskRadar = lazy(() => import('./pages/RiskRadar'));
const RegulatoryIntelligence = lazy(() => import('./pages/RegulatoryIntelligence'));
const Playbooks = lazy(() => import('./pages/Playbooks'));
const Departments = lazy(() => import('./pages/Departments'));
const ChatLogs = lazy(() => import('./pages/ChatLogs'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminAudit = lazy(() => import('./pages/AdminAudit'));
const AdminProgrammeOrders = lazy(() => import('./pages/AdminProgrammeOrders'));
const AdminMFA = lazy(() => import('./pages/AdminMFA'));
const MFASetupForced = lazy(() => import('./pages/MFASetupForced'));
const AdminLibrary = lazy(() => import('./pages/AdminLibrary'));
const AdminLibraryEditor = lazy(() => import('./pages/AdminLibraryEditor'));
const LibraryBrowse = lazy(() => import('./pages/LibraryBrowse'));
const LibraryDetail = lazy(() => import('./pages/LibraryDetail'));
const TrustCentre = lazy(() => import('./pages/TrustCentre'));
const ProcurementReadiness = lazy(() => import('./pages/ProcurementReadiness'));

/* ─── Lazy-loaded: global widgets (non-critical for first paint) ─── */
const CookieBanner = lazy(() => import('./components/CookieBanner').then(m => ({ default: m.CookieBanner })));
const ChatWidget = lazy(() => import('./components/ChatWidget').then(m => ({ default: m.ChatWidget })));

/* ─── Suspense fallback ─── */
const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-slate-400">Loading...</span>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <PageFallback />;
  if (!user) {
    // Show toast if this redirect was caused by session expiry
    if (sessionStorage.getItem('session_expired')) {
      sessionStorage.removeItem('session_expired');
      setTimeout(() => {
        const { toast } = require('sonner');
        toast.info('Your session has expired. Please sign in again.', { duration: 6000 });
      }, 300);
    }
    return <Navigate to="/" replace />;
  }
  // Beta invite scoping — block direct URL access to areas the tester wasn't granted.
  if (isBetaBlocked(user, pathname)) return <Navigate to="/dashboard" replace />;
  return children;
};

const PartnerRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { hasPartnerAccess } = useClient();
  if (loading) return <PageFallback />;
  if (!user) return <Navigate to="/" replace />;
  if (!hasPartnerAccess) return <Navigate to="/dashboard" replace />;
  return children;
};

function RouteWatcher() {
  const { pathname } = useLocation();
  useEffect(() => {
    scrollToTop('instant');
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

const ClientWorkspaceRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { isPartner, isInClientWorkspace } = useClient();
  const { pathname } = useLocation();
  if (loading) return <PageFallback />;
  if (!user) return <Navigate to="/" replace />;
  if (isPartner && !isInClientWorkspace && pathname === '/dashboard') return <Navigate to="/partner" replace />;
  return children;
};

/* Owner-only guard for Specialist Programme management surfaces (blueprints,
   programmes, programme courses). Mirrors the authoritative backend require_owner. */
const OwnerRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageFallback />;
  if (!user) return <Navigate to="/" replace />;
  if (user.role_in_org !== 'owner') return <Navigate to="/dashboard" replace />;
  return children;
};

/* Syncs the active brand colour (owner or client) to CSS custom properties for scrollbar theming */
function BrandingSync() {
  const { branding } = useBranding();
  const { activeClientOrg, isInClientWorkspace } = useClient();
  useEffect(() => {
    const color = (isInClientWorkspace && activeClientOrg?.branding_color)
      ? activeClientOrg.branding_color
      : (branding?.primary_color || '#4338ca');
    document.documentElement.style.setProperty('--scrollbar-thumb', color + '66');
    document.documentElement.style.setProperty('--scrollbar-thumb-hover', color + 'cc');
    document.documentElement.style.setProperty('--brand-color', color);
  }, [branding?.primary_color, activeClientOrg?.branding_color, isInClientWorkspace]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
    <HelmetProvider>
    <ThemeProvider>
      <AuthProvider>
        <BrandingProvider>
          <ClientProvider>
            <PlanPreviewProvider>
            <BrowserRouter>
              <RouteWatcher />
              <CommercialTracker />
              <BrandingSync />
              <SiteAccessGate>
              <Suspense fallback={<PageFallback />}>
              <Routes>
                {/* Public / standalone routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/support" element={<Support />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/features" element={<Features />} />
                <Route path="/about" element={<About />} />
                <Route path="/professional-standards-institute" element={<ProfessionalStandardsInstitute />} />
                <Route path="/accreditation" element={<AccreditationHub />} />
                <Route path="/accreditation/registry" element={<AccreditationRegistry />} />
                <Route path="/verification" element={<VerificationHub />} />
                <Route path="/simplex-safety-solutions" element={<Navigate to="/professional-standards-institute" replace />} />
                <Route path="/what-is-cpdforge" element={<WhatIsCPDForge />} />
                <Route path="/what-is-an-ai-compliance-platform" element={<WhatIsAICompliancePlatform />} />
                <Route path="/knowledge-hub" element={<KnowledgeHub />} />
                <Route path="/knowledge-hub/admin/new" element={<ProtectedRoute><KnowledgeHubEditor /></ProtectedRoute>} />
                <Route path="/knowledge-hub/admin/edit/:slug" element={<ProtectedRoute><KnowledgeHubEditor /></ProtectedRoute>} />
                <Route path="/knowledge-hub/:slug" element={<KnowledgeHubArticle />} />
                <Route path="/keep-compliance-training-up-to-date" element={<KeepComplianceUpToDate />} />
                <Route path="/regulatory-change-tracking-software" element={<RegulatoryChangeTracking />} />
                <Route path="/ai-compliance-training-platform" element={<AIComplianceTrainingPlatform />} />
                <Route path="/ai-compliance-training" element={<Navigate to="/ai-compliance-training-platform" replace />} />
                <Route path="/ai-compliance-training-software" element={<Navigate to="/compliance-training-software" replace />} />
                <Route path="/compliance-training-platform-ai" element={<Navigate to="/ai-compliance-training-platform" replace />} />
                <Route path="/compliance-training-software" element={<ComplianceTrainingSoftware />} />
                <Route path="/ai-training-platform" element={<AITrainingPlatform />} />
                <Route path="/corporate-compliance-training-ai" element={<CorporateComplianceTrainingAI />} />
                <Route path="/why-compliance-training-fails" element={<WhyComplianceTrainingFails />} />
                <Route path="/how-to-improve-compliance-training" element={<HowToImproveComplianceTraining />} />
                <Route path="/compliance-training-effectiveness" element={<ComplianceTrainingEffectiveness />} />
                <Route path="/ai-course-generator-compliance" element={<Navigate to="/ai-course-creation-compliance" replace />} />
                <Route path="/ai-course-creation-compliance" element={<AICourseCreationCompliance />} />
                <Route path="/automated-compliance-training" element={<AutomatedComplianceTraining />} />
                <Route path="/ihasco-alternative" element={<IHASCOAlternative />} />
                <Route path="/high-speed-training-alternative" element={<HighSpeedTrainingAlternative />} />
                <Route path="/skillcast-alternative" element={<SkillcastAlternative />} />
                <Route path="/talentlms-alternative" element={<TalentLMSAlternative />} />
                <Route path="/litmos-alternative" element={<LitmosAlternative />} />
                <Route path="/docebo-alternative" element={<DoceboAlternative />} />
                <Route path="/diligent-esg-alternative" element={<DiligentESGAlternative />} />
                <Route path="/workiva-alternative" element={<WorkivaAlternative />} />
                <Route path="/coursera-esg-alternative" element={<CourseraESGAlternative />} />
                <Route path="/sphera-alternative" element={<SpheraAlternative />} />
                <Route path="/enablon-alternative" element={<EnablonAlternative />} />
                <Route path="/ecovadis-alternative" element={<EcoVadisAlternative />} />
                <Route path="/positiongreen-alternative" element={<PositionGreenAlternative />} />
                <Route path="/benchmarkesg-alternative" element={<BenchmarkESGAlternative />} />
                <Route path="/novata-alternative" element={<NovataAlternative />} />
                <Route path="/persefoni-alternative" element={<PersefoniAlternative />} />
                <Route path="/udemy-business-alternative" element={<UdemyBusinessAlternative />} />
                <Route path="/linkedin-learning-alternative" element={<LinkedInLearningAlternative />} />
                <Route path="/skillsoft-alternative" element={<SkillsoftAlternative />} />
                <Route path="/go1-alternative" element={<Go1Alternative />} />
                <Route path="/opensesame-alternative" element={<OpenSesameAlternative />} />
                <Route path="/coursera-business-alternative" element={<CourseraBusinessAlternative />} />
                <Route path="/edx-business-alternative" element={<EdXBusinessAlternative />} />
                <Route path="/pluralsight-alternative" element={<PluralsightAlternative />} />
                <Route path="/futurelearn-alternative" element={<FutureLearnAlternative />} />
                <Route path="/alison-alternative" element={<AlisonAlternative />} />
                <Route path="/human-focus-alternative" element={<HumanFocusAlternative />} />
                <Route path="/essentialskillz-alternative" element={<EssentialSkillzAlternative />} />
                <Route path="/alcumus-alternative" element={<AlcumusAlternative />} />
                <Route path="/safetyhub-alternative" element={<SafetyhubAlternative />} />
                <Route path="/rospa-alternative" element={<RoSPAAlternative />} />
                <Route path="/praxis42-alternative" element={<Praxis42Alternative />} />
                <Route path="/deltanet-alternative" element={<DeltaNetAlternative />} />
                <Route path="/astutis-alternative" element={<AstutisAlternative />} />
                <Route path="/vinciworks-alternative" element={<VinciWorksAlternative />} />
                <Route path="/navex-alternative" element={<NavexAlternative />} />
                <Route path="/traliant-alternative" element={<TraliantAlternative />} />
                <Route path="/sai360-alternative" element={<SAI360Alternative />} />
                <Route path="/gan-integrity-alternative" element={<GANIntegrityAlternative />} />
                <Route path="/360factors-alternative" element={<ThreeSixtyFactorsAlternative />} />
                <Route path="/onetrust-alternative" element={<OneTrustAlternative />} />
                
                {/* Assessment Tool Routes */}
                <Route path="/compliance-training-assessment" element={<AssessmentLanding />} />
                <Route path="/compliance-training-assessment/quiz" element={<AssessmentV2 />} />
                <Route path="/compliance-training-assessment/quiz-v1" element={<AssessmentFlow />} />
                <Route path="/compliance-training-assessment/calculating" element={<AssessmentLoading />} />
                <Route path="/compliance-training-assessment/results" element={<AssessmentResults />} />
                <Route path="/compliance-training-assessment/methodology" element={<AssessmentMethodology />} />
                
                {/* Use Case Pages */}
                <Route path="/best-compliance-training-for-small-business" element={<BestComplianceTrainingForSmallBusiness />} />
                <Route path="/best-compliance-training-platform-for-smes" element={<BestComplianceTrainingPlatformForSMEs />} />
                <Route path="/best-compliance-training-with-ai" element={<BestComplianceTrainingWithAI />} />
                <Route path="/best-compliance-training-for-financial-services" element={<BestComplianceTrainingForFinancialServices />} />
                <Route path="/best-lms-for-onboarding" element={<BestLMSForOnboarding />} />
                <Route path="/best-accredited-compliance-training" element={<BestAccreditedComplianceTraining />} />
                <Route path="/best-esg-reporting-software" element={<BestESGReportingSoftware />} />
                <Route path="/best-training-platforms-for-remote-teams" element={<BestTrainingPlatformsForRemoteTeams />} />
                
<Route path="/hyperproof-alternative" element={<HyperproofAlternative />} />
                <Route path="/logicgate-alternative" element={<LogicGateAlternative />} />
                <Route path="/learnupon-alternative" element={<LearnUponAlternative />} />
                <Route path="/absorb-lms-alternative" element={<AbsorbLMSAlternative />} />
                <Route path="/ispring-learn-alternative" element={<ISpringLearnAlternative />} />
                <Route path="/cornerstone-alternative" element={<CornerstoneAlternative />} />
                <Route path="/moodle-alternative" element={<MoodleAlternative />} />
                <Route path="/sap-successfactors-alternative" element={<SAPSuccessFactorsAlternative />} />
                <Route path="/360learning-alternative" element={<ThreeSixtyLearningAlternative />} />
                <Route path="/best-compliance-training-platforms" element={<ComparisonHub />} />
                <Route path="/compare/health-safety-training-providers" element={<HealthSafetyPillar />} />
                <Route path="/compare/corporate-compliance-training-platforms" element={<ComplianceGRCPillar />} />
                <Route path="/compare/learning-management-systems" element={<LMSPillar />} />
                <Route path="/compare/esg-sustainability-platforms" element={<ESGPillar />} />
                <Route path="/compare/general-training-platforms" element={<GeneralTrainingPillar />} />
                <Route path="/features/microlearning" element={<MicrolearningFeature />} />
                <Route path="/guides/microlearning-training-guide" element={<MicrolearningGuide />} />
                <Route path="/help/mfa-guide" element={<MFAGuide />} />
                <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                <Route path="/shared/:shareId" element={<SharedCourseView />} />
                <Route path="/review/:token" element={<ProgrammeReview />} />
                <Route path="/demo" element={<DemoCourse />} />
                <Route path="/verify/sample" element={<VerifySample />} />
                <Route path="/verify/:certId" element={<VerifyCertificate />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/trust" element={<TrustCentre />} />

                {/* Public Library (no auth required) */}
                <Route path="/library" element={<PublicLibrary />} />
                <Route path="/library/programmes" element={<PublicProgrammes />} />
                <Route path="/library/programme/:slug" element={<PublicProgrammeDetail />} />
                <Route path="/credentials" element={<PublicCredentialsIndex />} />
                <Route path="/credentials/:family" element={<PublicCredentialFamily />} />
                <Route path="/programmes/cluster/:slug" element={<PublicClusterPage />} />
                <Route path="/for/:segment" element={<PublicSegmentPage />} />
                <Route path="/programme-purchase/success" element={<ProgrammePurchaseSuccess />} />
                <Route path="/programme-access/:programmeId" element={<PurchaseAccess />} />
                <Route path="/money-back-guarantee" element={<MoneyBackGuarantee />} />
                <Route path="/library/course/:slug" element={<PublicCourseDetail />} />
                <Route path="/library/pillar/:slug" element={<PublicPillarPage />} />
                <Route path="/library/industry/:slug" element={<PublicIndustryPage />} />
                <Route path="/library/regulation/:slug" element={<PublicRegulationPage />} />

                {/* Community Access — free public educational projection (CA2) */}
                <Route path="/community" element={<CommunityCatalogue />} />
                <Route path="/community/programme/:slug" element={<CommunityProgramme />} />
                <Route path="/community/course/:courseId" element={<CommunityCourse />} />
                <Route path="/community/course/:courseId/lesson/:lessonId" element={<CommunityLesson />} />

                {/* AI Governance & Assurance authority ecosystem (Phase C2) */}
                <Route path="/ai" element={<AIPillarHub />} />
                <Route path="/ai/standards/:slug" element={<AIStandardHub />} />
                <Route path="/ai/regulations/:slug" element={<AIRegulationHub />} />
                <Route path="/ai/roles/:slug" element={<AIRoleHub />} />
                <Route path="/ai/:objective" element={<AIObjectiveHub />} />

                {/* P1.6 — generic Subject Hub (AI uses its own ecosystem above; /subjects/ai redirects there) */}
                <Route path="/subjects/ai" element={<Navigate to="/ai" replace />} />
                <Route path="/subjects/:subject" element={<SubjectHub />} />

                {/* Learner Portal */}
                <Route path="/learner/login" element={<LearnerLogin />} />
                <Route path="/learner/set-password" element={<LearnerSetPassword />} />
                <Route path="/learner/courses" element={<LearnerCourses />} />
                <Route path="/learner/course/:assignmentId" element={<LearnerCoursePlayer />} />
                {/* Admin pages with shared sidebar layout */}
                <Route element={<AppLayout />}>
                  <Route path="/dashboard" element={<ClientWorkspaceRoute><Dashboard /></ClientWorkspaceRoute>} />
                  <Route path="/courses" element={<ClientWorkspaceRoute><Dashboard /></ClientWorkspaceRoute>} />
                  <Route path="/generate" element={<ClientWorkspaceRoute><GenerateCourse /></ClientWorkspaceRoute>} />
                  <Route path="/courses/:courseId" element={<ClientWorkspaceRoute><CourseView /></ClientWorkspaceRoute>} />
                  <Route path="/courses/:courseId/edit" element={<ClientWorkspaceRoute><EditCourse /></ClientWorkspaceRoute>} />
                  <Route path="/settings/branding" element={<ClientWorkspaceRoute><BrandingSettings /></ClientWorkspaceRoute>} />
                  <Route path="/settings/certificates" element={<ClientWorkspaceRoute><CertificateSettings /></ClientWorkspaceRoute>} />
                  <Route path="/settings/organisation" element={<ClientWorkspaceRoute><OrganisationProfile /></ClientWorkspaceRoute>} />
                  <Route path="/settings/ai" element={<ClientWorkspaceRoute><AISettings /></ClientWorkspaceRoute>} />
                  <Route path="/compliance" element={<ClientWorkspaceRoute><ComplianceReports /></ClientWorkspaceRoute>} />
                  <Route path="/templates" element={<ClientWorkspaceRoute><TemplateLibrary /></ClientWorkspaceRoute>} />
                  <Route path="/learners" element={<ClientWorkspaceRoute><Learners /></ClientWorkspaceRoute>} />
                  <Route path="/learners/:learnerId" element={<ClientWorkspaceRoute><LearnerProfile /></ClientWorkspaceRoute>} />
                  <Route path="/roles" element={<ClientWorkspaceRoute><Roles /></ClientWorkspaceRoute>} />
                  <Route path="/roles/:roleId" element={<ClientWorkspaceRoute><RoleDetail /></ClientWorkspaceRoute>} />
                  <Route path="/programmes" element={<ClientWorkspaceRoute><OwnerRoute><Programmes /></OwnerRoute></ClientWorkspaceRoute>} />
                  <Route path="/programmes/:programmeId" element={<ClientWorkspaceRoute><OwnerRoute><ProgrammeDetail /></OwnerRoute></ClientWorkspaceRoute>} />
                  <Route path="/blueprints" element={<ClientWorkspaceRoute><OwnerRoute><Blueprints /></OwnerRoute></ClientWorkspaceRoute>} />
                  <Route path="/blueprints/:blueprintId" element={<ClientWorkspaceRoute><OwnerRoute><BlueprintDetail /></OwnerRoute></ClientWorkspaceRoute>} />
                  <Route path="/programme-courses" element={<ClientWorkspaceRoute><OwnerRoute><ProgrammeCourses /></OwnerRoute></ClientWorkspaceRoute>} />
                  <Route path="/programme-audit" element={<ClientWorkspaceRoute><OwnerRoute><OwnerAudit /></OwnerRoute></ClientWorkspaceRoute>} />
                  <Route path="/education/programmes" element={<ClientWorkspaceRoute><OwnerRoute><EducationProgrammes /></OwnerRoute></ClientWorkspaceRoute>} />
                  <Route path="/education/programmes/:programmeId/detail" element={<ClientWorkspaceRoute><OwnerRoute><EducationProgrammeDetail /></OwnerRoute></ClientWorkspaceRoute>} />
                  <Route path="/education/studio/:blueprintId" element={<ClientWorkspaceRoute><OwnerRoute><EducationStudio /></OwnerRoute></ClientWorkspaceRoute>} />
                  <Route path="/education/readiness" element={<ClientWorkspaceRoute><OwnerRoute><EducationReadiness /></OwnerRoute></ClientWorkspaceRoute>} />
                  <Route path="/growth" element={<OwnerRoute><GrowthCommandCentre /></OwnerRoute>} />
                  <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/partner" element={<PartnerRoute><PartnerDashboard /></PartnerRoute>} />
                  <Route path="/partner/clients/:clientOrgId/members" element={<PartnerRoute><ClientMembers /></PartnerRoute>} />
                  <Route path="/partner/clients/:clientOrgId/teams" element={<PartnerRoute><ClientTeams /></PartnerRoute>} />
                  <Route path="/clients/compliance" element={<PartnerRoute><ClientComplianceOverview /></PartnerRoute>} />
                  <Route path="/compliance/risk-radar" element={<ProtectedRoute><RiskRadar /></ProtectedRoute>} />
                  <Route path="/compliance/regulatory-intelligence" element={<ProtectedRoute><RegulatoryIntelligence /></ProtectedRoute>} />
                  <Route path="/playbooks" element={<ProtectedRoute><Playbooks /></ProtectedRoute>} />
                  <Route path="/departments" element={<ClientWorkspaceRoute><Departments /></ClientWorkspaceRoute>} />
                  <Route path="/admin/chat-logs" element={<ProtectedRoute><ChatLogs /></ProtectedRoute>} />
                  <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
                  <Route path="/admin/audit" element={<ProtectedRoute><AdminAudit /></ProtectedRoute>} />
                  <Route path="/admin/programme-orders" element={<ProtectedRoute><AdminProgrammeOrders /></ProtectedRoute>} />
                  <Route path="/admin/mfa" element={<ProtectedRoute><AdminMFA /></ProtectedRoute>} />
                  <Route path="/mfa-setup" element={<MFASetupForced />} />
                  <Route path="/admin/library" element={<ProtectedRoute><AdminLibrary /></ProtectedRoute>} />
                  <Route path="/admin/library/:courseId" element={<ProtectedRoute><AdminLibraryEditor /></ProtectedRoute>} />
                  <Route path="/admin/procurement-readiness" element={<ProtectedRoute><ProcurementReadiness /></ProtectedRoute>} />
                  <Route path="/library/browse" element={<ProtectedRoute><LibraryBrowse /></ProtectedRoute>} />
                  <Route path="/library/browse/:courseId" element={<ProtectedRoute><LibraryDetail /></ProtectedRoute>} />
                </Route>
              </Routes>
              </Suspense>
              <Toaster position="bottom-right" />
              <Suspense fallback={null}>
                <CookieBanner />
                <ChatWidget />
              </Suspense>
              </SiteAccessGate>
            </BrowserRouter>
            </PlanPreviewProvider>
          </ClientProvider>
        </BrandingProvider>
      </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
